/**
 * Please note! This file is a thin layer around the API, but as opposed to the `api.ts` file
 * this one can be manually extended, especially to handle cases where Swagger is not enough.
 * Do not add business logic here.
 * If for some reason you have to add types from other places, try to avoid circular dependencies.
 * By default, add types to models.ts file.
 */

import Axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import { useState } from 'react';
import useSWR from 'swr';

import { FAVORITES_CACHE_KEY } from '#/client/constants';
import { ApiError } from '#/errors/serviceErrors';
import { HttpHeader } from '#/service/constants';
import { PlaybackInfoResponse } from '#/service/features/playback/types';
import { getDeviceInfo } from '#/service/user/userSessionManager';
import { changeUrlParams } from '#/service/utils';

import { axios } from './axios';
import {
  Collection,
  FeedResponse,
  PageLayout,
  ResourceItem,
  ResourceType,
  SwrConfig,
  UpNextResponse,
} from './models';
import { makeSwrUnauthorizedRetry } from './utils';

export function useDetailsResource(
  isLoggedIn: boolean,
  resourceType?: ResourceType,
  resourceId?: string,
  itemUrl?: string,
  $config?: SwrConfig
) {
  const url = itemUrl ?? `/api/core/catalog/${resourceType}/${resourceId}`;
  const { axios: $axiosConf, ...config } = $config || {
    revalidateOnFocus: false,
  };

  const { data, error, mutate } = useSWR<ResourceItem | Collection, Error>(
    [url, isLoggedIn],
    () => simpleGet<ResourceItem | Collection>(url, $axiosConf),
    config
  );

  return {
    data,
    isLoading: !error && !data,
    error: error as ApiError | undefined,
    mutate,
  };
}

export function usePageLayout(pageId?: string, $config?: SwrConfig) {
  const [finishedRevalidating, setFinishedRevalidating] = useState(false);
  const url = `/api/core/page/${pageId}?form-factor=desktop,tablet,mobile`;
  const { axios: $axiosConf, ...config } = $config || {
    revalidateOnFocus: false,
  };

  const onErrorRetry = makeSwrUnauthorizedRetry(setFinishedRevalidating);

  const { data, error, mutate } = useSWR<PageLayout, ApiError>(
    pageId ? url : null,
    () => simpleGet<PageLayout>(url, $axiosConf),
    {
      ...config,
      onErrorRetry,
    }
  );

  return {
    data,
    isLoading: !error && !data,
    error: finishedRevalidating && error,
    mutate,
  };
}

export const resourceService = {
  get(
    resourceType?: ResourceType,
    resourceId?: string,
    itemUrl?: string,
    $config?: AxiosRequestConfig
  ) {
    const url = itemUrl ?? `/api/core/catalog/${resourceType}/${resourceId}`;

    return axios.request<ResourceItem | Collection>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  play(
    resourceType?: ResourceType,
    resourceId?: string,
    playbackUrl?: string,
    $config?: AxiosRequestConfig
  ): AxiosPromise<PlaybackInfoResponse> {
    const url = playbackUrl ?? `/api/core/play/${resourceType}/${resourceId}`;

    return axios.request({
      url,
      method: 'GET',
      ...$config,
    });
  },

  getUpNext(
    url: string,
    $config?: AxiosRequestConfig
  ): AxiosPromise<UpNextResponse> {
    return axios.request<UpNextResponse>({
      url,
      method: 'GET',
      ...$config,
    });
  },
};

export function useInitialFavoritesData() {
  const url = '/api/core/user/favorites';

  const { data, error, mutate } = useSWR<FeedResponse, Error>(
    FAVORITES_CACHE_KEY,
    () =>
      axios
        .request({
          url,
          method: 'GET',
          params: {
            page: 1,
          },
        })
        .then((resp) => resp.data)
        // Fall back to empty response when the request fails
        .catch((): FeedResponse => ({ data: [] })),
    { shouldRetryOnError: false }
  );

  return {
    data,
    isLoading: !error && !data,
    error: error,
    mutate,
  };
}

export function simpleGet<T>(url: string, $axiosConf?: AxiosRequestConfig) {
  return axios.get<T>(url, $axiosConf).then((resp) => resp?.data);
}

/**
 * Send concurrency request. It uses different Axios instance, so it's not gonna use our interceptors
 * Additionally, concurrency request is silent, so it won't trigger any errors. Fire and forget.
 */
export function sendConcurrencyEvent(url: string, body?: object) {
  return Axios.post<void>(url, body, {
    headers: {
      [HttpHeader.DeviceInfo]: JSON.stringify(getDeviceInfo()),
    },
  })
    .then((res) => res.data)
    .catch(() => {});
}

export const endpointUrls = {
  getWatchHistory: (page = 1, pageSize = 20) =>
    pathWithParams('/api/core/user/watch-history', {
      page,
      pageSize,
    }),
  getFavorites: (page = 1, pageSize = 20) =>
    pathWithParams('/api/core/user/favorites', {
      page,
      pageSize,
    }),
  search: (query: string, page = 1, pageSize = 20) =>
    pathWithParams('/api/core/search', {
      q: query,
      page,
      pageSize,
    }),
};

export const pathWithParams = (
  path: string,
  queryParams = {},
  baseQuery: string = window.location.search,
  keysToPersist: string[] = [
    'orgHash',
    'variationHash',
    'appId',
    'env',
    'countryCode',
    'regionCode',
  ]
) => {
  const urlParams = new URLSearchParams(baseQuery);

  const mergedParams = keysToPersist.reduce(
    (acc, p) => (urlParams.has(p) ? { ...acc, [p]: urlParams.get(p) } : acc),
    queryParams
  );

  return changeUrlParams(path, mergedParams);
};
